import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Instagram, Clock, Terminal, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        business_name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputFields = [
    { name: 'name', placeholder: 'Your Name', required: true, type: 'text' },
    { name: 'business_name', placeholder: 'Business Name', required: true, type: 'text' },
    { name: 'email', placeholder: 'Email Address', required: true, type: 'email' },
    { name: 'phone', placeholder: 'Phone (optional)', required: false, type: 'tel' },
  ];

  return (
    <section id="contact" className="py-32 md:py-40 bg-[#0a0a0a] relative overflow-hidden" data-testid="contact-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-5" />
      
      {/* Animated line */}
      <motion.div
        className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#e62020]/50 to-transparent"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[#e62020] text-xs tracking-[0.3em] uppercase mb-6">
              Contact
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              data-testid="contact-headline"
            >
              Ready to Get Your Business{' '}
              <span className="text-[#e62020]">Online?</span>
            </h2>
            <p className="text-[#888888] text-base mb-10 leading-relaxed">
              No website? No problem. We'll build it. Reach out and let's talk about what your business needs.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <motion.a
                href="mailto:VortexWebCo@gmail.com"
                className="flex items-center gap-4 text-[#888888] hover:text-white transition-colors group p-4 border border-[#262626] hover:border-[#e62020]/30 bg-[#111111]"
                data-testid="contact-email"
                whileHover={{ x: 5 }}
              >
                <div className="w-10 h-10 flex items-center justify-center border border-[#262626] group-hover:border-[#e62020]/40 transition-colors">
                  <Mail size={18} className="text-[#e62020]" />
                </div>
                <div>
                  <p className="text-xs text-[#888888] font-mono">EMAIL</p>
                  <span className="text-sm text-white">VortexWebCo@gmail.com</span>
                </div>
              </motion.a>

              <motion.a
                href="https://www.instagram.com/vortexwebco/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-[#888888] hover:text-white transition-colors group p-4 border border-[#262626] hover:border-[#e62020]/30 bg-[#111111]"
                data-testid="contact-instagram"
                whileHover={{ x: 5 }}
              >
                <div className="w-10 h-10 flex items-center justify-center border border-[#262626] group-hover:border-[#e62020]/40 transition-colors">
                  <Instagram size={18} className="text-[#e62020]" />
                </div>
                <div>
                  <p className="text-xs text-[#888888] font-mono">INSTAGRAM</p>
                  <span className="text-sm text-white">@vortexwebco</span>
                </div>
              </motion.a>

              <div className="flex items-center gap-4 p-4 border border-[#262626] bg-[#111111]">
                <div className="w-10 h-10 flex items-center justify-center border border-[#262626]">
                  <Clock size={18} className="text-[#888888]" />
                </div>
                <div>
                  <p className="text-xs font-mono text-[#888888]">RESPONSE TIME</p>
                  <span className="text-sm text-white">Usually within 24 hours</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
          >
            {/* Terminal-style form container */}
            <div className="bg-[#111111] border border-[#262626] overflow-hidden">
              {/* Terminal header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626] bg-[#0d0d0d]">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#e62020]/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-[10px] font-mono text-[#888888]">contact_form.exe</span>
                </div>
                <Terminal size={14} className="text-[#888888]" />
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="contact-form">
                {inputFields.map((field, index) => (
                  <motion.div
                    key={field.name}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className={`flex items-center border transition-colors ${
                      focusedField === field.name ? 'border-[#e62020]/50' : 'border-[#262626]'
                    }`}>
                      <span className="text-[#e62020] font-mono text-sm px-3">{'>'}</span>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        onFocus={() => setFocusedField(field.name)}
                        onBlur={() => setFocusedField(null)}
                        placeholder={field.placeholder + (field.required ? ' *' : '')}
                        required={field.required}
                        className="flex-1 bg-transparent text-white placeholder-[#888888] focus:outline-none py-4 pr-4 text-sm"
                        data-testid={`contact-input-${field.name.replace('_', '-')}`}
                      />
                    </div>
                  </motion.div>
                ))}

                {/* Message textarea */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="relative"
                >
                  <div className={`border transition-colors ${
                    focusedField === 'message' ? 'border-[#e62020]/50' : 'border-[#262626]'
                  }`}>
                    <div className="flex items-start">
                      <span className="text-[#e62020] font-mono text-sm px-3 pt-4">{'>'}</span>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="What do you need? *"
                        required
                        rows={4}
                        className="flex-1 bg-transparent text-white placeholder-[#888888] focus:outline-none py-4 pr-4 text-sm resize-none"
                        data-testid="contact-input-message"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#e62020] text-white py-4 font-bold uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
                  data-testid="contact-submit"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div 
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                      <span className="font-mono text-sm">SENDING...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span className="font-mono text-sm">SEND MESSAGE</span>
                    </>
                  )}
                  <motion.div
                    className="absolute inset-0 bg-white/10"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-500/10 border border-green-500/30 text-green-400 text-center font-mono text-sm"
                    data-testid="contact-success"
                  >
                    <span className="text-green-500">{'>'}</span> Message sent successfully! We'll get back to you soon.
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-center font-mono text-sm"
                    data-testid="contact-error"
                  >
                    <span className="text-red-500">{'>'}</span> Error: Please try again or email us directly.
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
